import services from 'services/services';
import { chain, orderBy } from 'lodash';
import { QUERY_ORDER_DIR } from 'helpers/variables';

// Import api
import { GET_AGREEMENTS_API } from 'helpers/api';

// Import helpers
import { replaceStringChar } from 'helpers';
import { ENABLED_OBLIGATORY_AGREEMENTS } from 'helpers/agreements';

export const fetchAgreementsByType = (type) => {
	const url = replaceStringChar(GET_AGREEMENTS_API, { ':type': type });
	return services.get(`${url}?system=${process.env.REACT_APP_SYSTEM}`);
};

export const getObligatoryAgreementsVersionIDs = (agreements) =>
	agreements
		.filter(
			({ obligatory, type }) =>
				!ENABLED_OBLIGATORY_AGREEMENTS.includes(type) && obligatory
		)
		.map(({ version }) => version.id);

export const getConfirmedAgreementsVersionIDs = (agreements) =>
	agreements
		.filter(({ subscriber_agreement }) => subscriber_agreement?.confirmed)
		.map(({ version }) => version.id);

export const getSingularAgreementVersion = (agreements, properties) => {
	const agr = [];
	const groupes = chain(agreements)
		.groupBy('id')
		.sortBy((group) => agreements.indexOf(group[0]))
		.toPairs()
		.value();
	groupes.forEach(([_, arr]) => {
		if (arr.length <= 1) {
			agr.push(arr[0]);
		} else {
			const versions = orderBy(arr, properties, QUERY_ORDER_DIR.malejaco);
			agr.push(versions[0]);
		}
	});
	const acceptedVersions = getConfirmedAgreementsVersionIDs(agr);
	return { agreements: agr, acceptedVersions };
};

export const getAgreementsByType = (agreementsToFetch) => {
	const handleFetchAgreementsByType = (type) => fetchAgreementsByType(type);

	return Promise.all(agreementsToFetch.map(handleFetchAgreementsByType)).then(
		(response) => response.map(({ data }) => data).flat()
	);
};

export const filterAgreementsByType = (agreements, agreementsTypes) =>
	agreements.filter(({ type }) =>
		agreementsTypes.find((subType) => type === subType)
	);

export const getUniqueAgreements = (array) =>
	array.filter(
		({ version }, index, arr) =>
			arr.findIndex((agreement) => agreement.version.id === version.id) ===
			index
	);

export const filterConfirmedAgreementsByType = (agreements, agreementsTypes) =>
	agreements.filter(
		({ type, subscriber_agreement }) =>
			!agreementsTypes.find(
				(subType) => type === subType && subscriber_agreement?.confirmed
			)
	);

export const filterConfirmedAgreementsByIdAndType = (
	agreements,
	subscriberAgreements,
	initialAcceptedToRemove
) =>
	agreements.filter(
		({ type, version }) =>
			!subscriberAgreements.find(
				({ subscriber_agreement }) =>
					version.id === subscriber_agreement?.agreement_version_id &&
					subscriber_agreement?.confirmed &&
					initialAcceptedToRemove.includes(type)
			)
	);

export const filterConfirmedAgreementsByID = (
	agreements,
	subscriberAgreements
) =>
	agreements.filter(
		({ version }) =>
			!subscriberAgreements.find(
				({ subscriber_agreement }) =>
					version.id === subscriber_agreement?.agreement_version_id &&
					subscriber_agreement?.confirmed
			)
	);

export const removeInitialAcceptedAgreements = (agreements) =>
	agreements.filter(
		({ subscriber_agreement }) => !subscriber_agreement?.confirmed
	);

export const changeAgreementsConfirmedStatus = ({
	prevAgreements,
	agreementVersionIDs,
	confirmed
}) =>
	prevAgreements.map((prevAgreement) =>
		agreementVersionIDs.includes(prevAgreement.version.id)
			? {
					...prevAgreement,
					subscriber_agreement: {
						...prevAgreement.subscriber_agreement,
						confirmed
					}
			  }
			: prevAgreement
	);

export const getFilteredAgreementsIDs = (acceptedIDs, id) => {
	const isAlreadyAccepted = acceptedIDs.includes(id);
	const filteredAgreements = acceptedIDs.filter((item) => item !== id);

	return isAlreadyAccepted ? filteredAgreements : [...acceptedIDs, id];
};
